// Chemin: C:\gestion-copro\src\services\auth.service.ts
// Service d'authentification pour ORION PILOT
// Session 56 : correction isConseilSyndical pour couvrir tous les rôles CS
// Session 212 : ajout rôle 'coordinateur' dans ROLES_CONSEIL_SYNDICAL

import { 
  signInWithEmailAndPassword, 
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User as FirebaseUser,
  sendPasswordResetEmail
} from 'firebase/auth';
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc,
  Timestamp 
} from 'firebase/firestore';
import { auth, db } from './firebase';
import { UserProfile, UserRole } from '../types/user.types';

// ============================================
// AUTHENTIFICATION
// ============================================

/**
 * Connexion avec email et mot de passe
 */
export const signIn = async (email: string, password: string): Promise<FirebaseUser> => {
  const result = await signInWithEmailAndPassword(auth, email, password);
  
  // Mettre à jour lastLogin
  const userRef = doc(db, 'users', result.user.uid);
  const userDoc = await getDoc(userRef);
  
  if (userDoc.exists()) {
    await updateDoc(userRef, {
      lastLogin: Timestamp.now()
    });
  }
  
  return result.user;
};

/**
 * Déconnexion
 */
export const signOut = async (): Promise<void> => {
  await firebaseSignOut(auth);
};

/**
 * Envoyer un email de réinitialisation de mot de passe
 */
export const resetPassword = async (email: string): Promise<void> => {
  await sendPasswordResetEmail(auth, email);
};

/**
 * Observer les changements d'état d'authentification
 */
export const onAuthChange = (callback: (user: FirebaseUser | null) => void) => {
  return onAuthStateChanged(auth, callback);
};

// ============================================
// PROFIL UTILISATEUR
// ============================================

/**
 * Récupérer le profil utilisateur depuis Firestore
 */
export const getUserProfile = async (uid: string): Promise<UserProfile | null> => {
  const userRef = doc(db, 'users', uid);
  const userDoc = await getDoc(userRef);
  
  if (!userDoc.exists()) {
    return null;
  }
  
  return {
    id: userDoc.id,
    ...userDoc.data()
  } as UserProfile;
};

/**
 * Créer un profil utilisateur dans Firestore
 * À appeler après la création du compte dans Firebase Auth
 */
export const createUserProfile = async (
  uid: string,
  email: string,
  nom: string,
  prenom: string,
  role: UserRole = 'coproprietaire'
): Promise<UserProfile> => {
  const now = Timestamp.now();
  
  const userData: Omit<UserProfile, 'id'> = {
    email,
    nom,
    prenom,
    role,
    preferences: {
      notifications: true,
      emailRecap: true,
      langue: 'fr'
    },
    lastLogin: now,
    createdAt: now,
    updatedAt: now
  };
  
  const userRef = doc(db, 'users', uid);
  await setDoc(userRef, userData);
  
  return {
    id: uid,
    ...userData
  };
};

/**
 * Mettre à jour le profil utilisateur
 */
export const updateUserProfile = async (
  uid: string, 
  data: Partial<UserProfile>
): Promise<void> => {
  const userRef = doc(db, 'users', uid);
  await updateDoc(userRef, {
    ...data,
    updatedAt: Timestamp.now()
  });
};

// ============================================
// VÉRIFICATIONS DE RÔLE
// ============================================

/**
 * Rôles ayant accès à l'interface admin (/admin)
 * - gestionnaire : administrateur de la copropriété (secrétaire de fait)
 * - cs : membre du conseil syndical
 * - secretaire : secrétaire du CS (si utilisé comme rôle distinct)
 * - membre_cs : membre du CS (alias)
 */
const ROLES_CONSEIL_SYNDICAL: string[] = ['gestionnaire', 'cs', 'secretaire', 'membre_cs', 'coordinateur'];

/**
 * Vérifie si l'utilisateur est gestionnaire (admin principal)
 */
export const isGestionnaire = (user: UserProfile | null): boolean => {
  if (!user) return false;
  return user.role === 'gestionnaire' || user.isGestionnaireSaas === true;
};

/**
 * Vérifie si l'utilisateur est membre du CS ou gestionnaire
 * Donne accès à l'interface admin (/admin)
 */
export const isConseilSyndical = (user: UserProfile | null): boolean => {
  if (!user) return false;
  
  // Admin SaaS a toujours accès
  if (user.isGestionnaireSaas === true) return true;
  
  // Vérifier si le rôle fait partie du CS
  return ROLES_CONSEIL_SYNDICAL.includes(user.role);
};

/**
 * Vérifie si l'utilisateur est un simple copropriétaire (pas CS)
 */
export const isCoproprietaire = (user: UserProfile | null): boolean => {
  if (!user) return false;
  return user.role === 'coproprietaire';
};
